import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { fetchNamedIconSvg } from '@apis/storage';
import styles from './CardIcon.module.css';
import { LOCAL_FALLBACK, recolor, type ICardIconName } from './cardIconAssets';

export type { ICardIconName };

type ICardIconProps = {
  name: ICardIconName;
  size?: number;
};

/**
 * Mirrors trusstor's TrusstorIconShared: pulls the canonical SVG from
 * the `trusstor-icons` bucket via the d-orca storage proxy and inlines
 * it. Falls back to a vendored local copy if the bucket doesn't have
 * the asset for this dev env. SVG colours are normalised to
 * `currentColor` so the icon picks up `var(--navy)` from the header.
 *
 * Uses a plain `fetch`-on-mount instead of TanStack Query because this
 * component is rendered in unit tests that don't set up a
 * QueryClientProvider — and the icon's caching needs are trivial
 * (one fetch per page-mount per icon name).
 */
export function CardIcon({ name, size = 22 }: ICardIconProps) {
  const { siteId } = useParams<{ siteId: string }>();
  const [remote, setRemote] = useState<string | null>(null);

  useEffect(() => {
    if (!siteId) return;
    const ctrl = new AbortController();
    fetchNamedIconSvg(siteId, name, ctrl.signal)
      .then((svg) => setRemote(svg))
      .catch(() => setRemote(null));
    return () => ctrl.abort();
  }, [siteId, name]);

  const html = useMemo(() => recolor(remote ?? LOCAL_FALLBACK[name]), [remote, name]);

  return (
    <span
      className={styles.icon}
      aria-hidden="true"
      style={{ width: size, height: size }}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
